import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  CheckCircle2,
  Clock,
  Loader2,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Building2,
  GraduationCap,
  ArrowLeft,
  FolderKanban,
} from "lucide-react";
import DashboardLayout from "layouts/DashboardLayout";
import { dossierService } from "services/dossier.service";
import { ROUTES } from "constants/routes";
import type {
  Dossier,
  DossierStep,
  DossierStepDocument,
} from "types/dossier.types";
import {
  Badge,
  EmptyState,
  LoadingSpinner,
  ProgressBar,
  DOSSIER_STATUS_BADGE,
  STEP_STATUS_BADGE,
  STATUS_LABELS,
} from "components/dashboard";
import type {
  BadgeVariant,
  StepStatusKey,
  DossierStatusKey,
} from "components/dashboard";

const C_PRIMARY = "#ff6c00";

// ─── Styled ────────────────────────────────────────────────────────────────────

const DossierHeader = styled.div`
  background: linear-gradient(135deg, #111 0%, #1f1f1f 100%);
  color: white;
  padding: 1.5rem 2rem;
  margin-bottom: 2rem;
  border: 1px solid #1a1a1a;
`;
const DestTitle = styled.h1`
  font-size: 1.4rem;
  font-weight: 900;
  margin-bottom: 0.3rem;
`;
const DestMeta = styled.p`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.5);
`;
const ProgressWrap = styled.div`
  margin-top: 1rem;
`;
const PLabel = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 0.4rem;
`;

const Timeline = styled.div`
  position: relative;
  &::before {
    content: "";
    position: absolute;
    left: 19px;
    top: 8px;
    bottom: 8px;
    width: 2px;
    background: #e5e7eb;
  }
`;
const TlItem = styled.div`
  position: relative;
  padding-left: 52px;
  margin-bottom: 16px;
`;
const TlDot = styled.div<{ $color: string }>`
  position: absolute;
  left: 8px;
  top: 14px;
  width: 22px;
  height: 22px;
  background: ${(p) => p.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.65rem;
  z-index: 1;
  box-shadow: 0 0 0 3px white;
`;
const StepCard = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  overflow: hidden;
`;
const StepHeader = styled.div<{ $clickable?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  cursor: ${(p) => (p.$clickable ? "pointer" : "default")};
  &:hover {
    background: ${(p) => (p.$clickable ? "#fafafa" : "inherit")};
  }
`;
const StepTitle = styled.div`
  font-size: 0.9rem;
  font-weight: 700;
  color: #111;
`;
const StepMeta = styled.div`
  font-size: 0.75rem;
  color: #9ca3af;
  margin-top: 2px;
`;
const StepBody = styled.div`
  border-top: 1px solid #f3f4f6;
  padding: 1rem 1.25rem;
`;

const DOC_STATUS_COLORS: Record<string, string> = {
  pending: "#9ca3af",
  uploaded: "#f97316",
  validated: "#22c55e",
  rejected: "#ef4444",
};
const DOC_STATUS_LABELS: Record<string, string> = {
  pending: "En attente",
  uploaded: "Fourni",
  validated: "Validé ✓",
  rejected: "Rejeté",
};

const DocList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;
const DocRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 0.75rem;
  background: #f9fafb;
  border: 1px solid #f3f4f6;
  gap: 0.5rem;
`;
const DocLabel = styled.div`
  font-size: 0.82rem;
  font-weight: 600;
  color: #374151;
  flex: 1;
`;
const DocSource = styled.span<{ $momseduc: boolean }>`
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  padding: 2px 7px;
  background: ${(p) => (p.$momseduc ? "#fff3e8" : "#f0fdf4")};
  color: ${(p) => (p.$momseduc ? C_PRIMARY : "#16a34a")};
  flex-shrink: 0;
`;
const DocStatusBadge = styled.span<{ $color: string }>`
  font-size: 0.7rem;
  font-weight: 700;
  color: ${(p) => p.$color};
  flex-shrink: 0;
`;

const DossierGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1px;
  background: #e5e7eb;
  border: 1px solid #e5e7eb;
`;
const DCard = styled.div`
  background: white;
  padding: 1.25rem;
  cursor: pointer;
  border: 2px solid transparent;
  transition:
    border-color 0.18s,
    box-shadow 0.18s;
  &:hover {
    border-color: ${C_PRIMARY};
    box-shadow: 0 4px 16px rgba(255, 108, 0, 0.12);
  }
`;
const BackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 1.25rem;
  padding: 0;
  &:hover {
    color: #111;
  }
`;
const AlertBox = styled.div<{ $variant: "error" | "info" }>`
  border: 1px solid ${(p) => (p.$variant === "error" ? "#fecaca" : "#bfdbfe")};
  background: ${(p) => (p.$variant === "error" ? "#fef2f2" : "#eff6ff")};
  padding: 0.6rem 0.75rem;
  font-size: 0.8rem;
  margin-bottom: 0.75rem;
  color: ${(p) => (p.$variant === "error" ? "#dc2626" : "#1d4ed8")};
`;

// ─── StepItem ──────────────────────────────────────────────────────────────────

const STEP_DOT_COLORS: Record<string, string> = {
  pending: "#9ca3af",
  in_progress: "#3b82f6",
  completed: "#22c55e",
  blocked: "#ef4444",
};

const StepItem: React.FC<{ step: DossierStep }> = ({ step }) => {
  const [open, setOpen] = useState(
    step.status === "in_progress" || step.status === "blocked",
  );
  const dotColor = STEP_DOT_COLORS[step.status] ?? "#9ca3af";
  const badgeVariant: BadgeVariant =
    STEP_STATUS_BADGE[step.status as StepStatusKey] ?? "neutral";
  const icon =
    step.status === "completed" ? (
      <CheckCircle2 size={11} />
    ) : step.status === "in_progress" ? (
      <Loader2 size={11} />
    ) : step.status === "blocked" ? (
      <AlertTriangle size={11} />
    ) : (
      <Clock size={11} />
    );

  return (
    <TlItem>
      <TlDot $color={dotColor}>{icon}</TlDot>
      <StepCard>
        <StepHeader
          $clickable={step.documents.length > 0}
          onClick={() => setOpen((o) => !o)}
        >
          <div>
            <StepTitle>{step.label}</StepTitle>
            <StepMeta>
              Étape {step.orderIndex}
              {step.deadline &&
                ` · Échéance : ${new Date(step.deadline).toLocaleDateString("fr-FR")}`}
              {step.completedAt &&
                ` · Validée le ${new Date(step.completedAt).toLocaleDateString("fr-FR")}`}
            </StepMeta>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <Badge variant={badgeVariant}>
              {STATUS_LABELS[step.status] ?? step.status}
            </Badge>
            {step.documents.length > 0 &&
              (open ? <ChevronUp size={15} /> : <ChevronDown size={15} />)}
          </div>
        </StepHeader>

        {open && (
          <StepBody>
            {step.blockedReason && (
              <AlertBox $variant="error">⚠️ {step.blockedReason}</AlertBox>
            )}
            {step.notes && <AlertBox $variant="info">📝 {step.notes}</AlertBox>}
            {step.documents.length > 0 && (
              <DocList>
                {step.documents.map((doc: DossierStepDocument) => (
                  <DocRow key={doc.id}>
                    <DocLabel>
                      {doc.required && (
                        <span style={{ color: "#ef4444" }}>* </span>
                      )}
                      {doc.label}
                      {doc.rejectionReason && (
                        <div
                          style={{
                            fontSize: "0.7rem",
                            color: "#ef4444",
                            marginTop: 2,
                          }}
                        >
                          {doc.rejectionReason}
                        </div>
                      )}
                    </DocLabel>
                    <DocSource $momseduc={doc.source === "momseduc"}>
                      {doc.source === "momseduc" ? (
                        <>
                          <Building2 size={9} style={{ display: "inline" }} />{" "}
                          Momseduc
                        </>
                      ) : (
                        <>
                          <GraduationCap
                            size={9}
                            style={{ display: "inline" }}
                          />{" "}
                          Étudiant
                        </>
                      )}
                    </DocSource>
                    <DocStatusBadge
                      $color={DOC_STATUS_COLORS[doc.status] ?? "#9ca3af"}
                    >
                      {DOC_STATUS_LABELS[doc.status] ?? doc.status}
                    </DocStatusBadge>
                  </DocRow>
                ))}
              </DocList>
            )}
          </StepBody>
        )}
      </StepCard>
    </TlItem>
  );
};

// ─── Main ──────────────────────────────────────────────────────────────────────

const ProceduresPage: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const [dossiers, setDossiers] = useState<Dossier[]>([]);
  const [selected, setSelected] = useState<Dossier | null>(null);
  const [loading, setLoading] = useState(true);

  const loadDossier = useCallback(async (dossierId: number) => {
    setLoading(true);
    try {
      const d = await dossierService.get(dossierId);
      setSelected(d);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (id) {
      loadDossier(Number(id));
    } else {
      dossierService.list().then((list) => {
        setDossiers(list);
        setLoading(false);
      });
    }
  }, [id, loadDossier]);

  if (loading) {
    return (
      <DashboardLayout title="Mon dossier">
        <LoadingSpinner />
      </DashboardLayout>
    );
  }

  // ── Liste ────────────────────────────────────────────────────────────────────
  if (!selected) {
    return (
      <DashboardLayout title="Mon dossier">
        {dossiers.length === 0 ? (
          <EmptyState
            icon={<FolderKanban size={40} />}
            title="Aucun dossier de parcours"
            description="Votre conseiller MomsEduc créera votre dossier après votre inscription."
          />
        ) : (
          <DossierGrid>
            {dossiers.map((d) => {
              const cfg: BadgeVariant =
                DOSSIER_STATUS_BADGE[d.status as DossierStatusKey] ?? "neutral";
              return (
                <DCard
                  key={d.id}
                  onClick={() =>
                    navigate(`${ROUTES.STUDENT_PROCEDURES}/${d.id}`)
                  }
                >
                  <div
                    style={{
                      fontSize: "1.2rem",
                      fontWeight: 900,
                      marginBottom: 6,
                    }}
                  >
                    {d.destination.flagEmoji} {d.destination.name}
                  </div>
                  <Badge variant={cfg}>
                    {STATUS_LABELS[d.status] ?? d.status}
                  </Badge>
                  <div style={{ marginTop: 12 }}>
                    <ProgressBar percent={d.progressPercent} />
                  </div>
                  <div
                    style={{
                      fontSize: "0.75rem",
                      color: "#9ca3af",
                      marginTop: 4,
                    }}
                  >
                    {d.progressPercent}% complété
                  </div>
                </DCard>
              );
            })}
          </DossierGrid>
        )}
      </DashboardLayout>
    );
  }

  // ── Détail ───────────────────────────────────────────────────────────────────
  const steps = (selected.steps ?? [])
    .slice()
    .sort((a, b) => a.orderIndex - b.orderIndex);

  return (
    <DashboardLayout title={`Dossier — ${selected.destination.name}`}>
      <BackBtn onClick={() => navigate(ROUTES.STUDENT_PROCEDURES)}>
        <ArrowLeft size={15} /> Tous les dossiers
      </BackBtn>

      <DossierHeader>
        <DestTitle>
          {selected.destination.flagEmoji} {selected.destination.name}
        </DestTitle>
        <DestMeta>
          Dossier #{selected.id} · Créé le{" "}
          {new Date(selected.createdAt).toLocaleDateString("fr-FR")} · Devise :{" "}
          {selected.destination.currency}
        </DestMeta>
        <ProgressWrap>
          <PLabel>
            <span>Progression</span>
            <span>{selected.progressPercent}%</span>
          </PLabel>
          <ProgressBar percent={selected.progressPercent} dark />
        </ProgressWrap>
      </DossierHeader>

      <Timeline>
        {steps.map((step) => (
          <StepItem key={step.id} step={step} />
        ))}
      </Timeline>
    </DashboardLayout>
  );
};

export default ProceduresPage;
